class CounterComponent extends HTMLElement {
  constructor() {
    super();
    this.count = 0;
    this.start = 0;
    this.target = 0;
    this.step = 1;
    this.interval = 5000; // 5 seconds in milliseconds
    this.delay = 0;
    this.started = false;
    this.intervalId = null;
    this.counterElement = null;
    this.observer = null;
    this.previousFormatted = '';
  }

  connectedCallback() {
    // Get the counter element
    this.counterElement = this.querySelector('.counter__number');
    
    if (!this.counterElement) return;

    // Get settings from data attributes
    this.start = parseInt(this.counterElement.dataset.start) || 0;
    this.target = parseInt(this.counterElement.dataset.target) || 0;
    this.step = parseInt(this.counterElement.dataset.step) || 1;
    this.interval = (parseInt(this.counterElement.dataset.interval) || 5) * 1000; // Convert seconds to milliseconds
    this.delay = parseInt(this.counterElement.dataset.delay) || 0;

    // Initialize count with a random number between start and target (inclusive)
    // If target is less than start, use start as minimum and target as maximum
    const min = Math.min(this.start, this.target);
    const max = Math.max(this.start, this.target);
    this.count = this.getRandomNumber(min, max);
    
    // Build initial digit containers
    const initialFormatted = this.count.toLocaleString();
    this.buildDigitContainers(initialFormatted);
    this.previousFormatted = initialFormatted;

    // Initialize the observer
    this.initObserver();
  }

  getRandomNumber(min, max) {
    // Ensure min is less than max
    if (min > max) {
      [min, max] = [max, min];
    }
    // Generate random number between min and max (inclusive)
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  disconnectedCallback() {
    // Cleanup
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initObserver() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.started) {
        this.startCounter();
        this.observer.disconnect();
      }
    }, {
      threshold: 0.1
    });

    this.observer.observe(this.counterElement);
  }

  updateCounter() {
    if (!this.counterElement) return;

    const formattedNumber = this.count.toLocaleString();
    const oldFormatted = this.previousFormatted || '';
    
    // If the formatted number structure changed (e.g., 999 to 1,000) or first time, rebuild
    if (formattedNumber.length !== oldFormatted.length || !oldFormatted) {
      this.buildDigitContainers(formattedNumber);
      this.previousFormatted = formattedNumber;
      return;
    }

    // Update individual digits
    const oldDigits = oldFormatted.split('');
    const newDigits = formattedNumber.split('');
    
    // Find digit containers
    const digitContainers = this.counterElement.querySelectorAll('.counter__digit-container');
    
    newDigits.forEach((newDigit, index) => {
      if (digitContainers[index]) {
        const oldDigit = oldDigits[index] || '';
        if (newDigit !== oldDigit && newDigit.match(/\d/)) {
          this.animateDigit(digitContainers[index], oldDigit, newDigit);
        } else if (newDigit !== oldDigit) {
          // Update non-digit characters (commas, etc.) directly
          digitContainers[index].textContent = newDigit;
        }
      }
    });

    this.previousFormatted = formattedNumber;
  }

  buildDigitContainers(formattedNumber) {
    // Clear existing content
    this.counterElement.innerHTML = '';
    
    const digits = formattedNumber.split('');
    digits.forEach((char, index) => {
      const container = document.createElement('span');
      container.className = 'counter__digit-container';
      
      if (char.match(/\d/)) {
        // It's a digit - create animated container
        const digitWrapper = document.createElement('span');
        digitWrapper.className = 'counter__digit-wrapper';
        const digitSpan = document.createElement('span');
        digitSpan.className = 'counter__digit';
        digitSpan.textContent = char;
        digitWrapper.appendChild(digitSpan);
        container.appendChild(digitWrapper);
      } else {
        // It's a separator (comma, etc.) - just display it
        container.className = 'counter__digit-container counter__digit-separator';
        container.textContent = char;
      }
      
      this.counterElement.appendChild(container);
    });
  }

  animateDigit(container, oldDigit, newDigit) {
    const wrapper = container.querySelector('.counter__digit-wrapper');
    if (!wrapper) return;

    // Get or create old digit element
    let oldDigitEl = wrapper.querySelector('.counter__digit');
    if (!oldDigitEl) {
      oldDigitEl = document.createElement('span');
      oldDigitEl.className = 'counter__digit';
      oldDigitEl.textContent = oldDigit;
      wrapper.appendChild(oldDigitEl);
    } else {
      oldDigitEl.textContent = oldDigit;
    }
    oldDigitEl.className = 'counter__digit counter__digit-old';

    // Create new digit element
    const newDigitEl = document.createElement('span');
    newDigitEl.className = 'counter__digit counter__digit-new';
    newDigitEl.textContent = newDigit;
    wrapper.appendChild(newDigitEl);
    
    // Trigger animation
    requestAnimationFrame(() => {
      wrapper.classList.add('counter__digit-animating');
    });

    // Clean up after animation
    setTimeout(() => {
      wrapper.classList.remove('counter__digit-animating');
      wrapper.innerHTML = '';
      const currentDigit = document.createElement('span');
      currentDigit.className = 'counter__digit';
      currentDigit.textContent = newDigit;
      wrapper.appendChild(currentDigit);
    }, 600);
  }

  incrementCounter() {
    if (this.count < this.target) {
      this.count = Math.min(this.count + this.step, this.target);
      this.updateCounter();
    } else {
      // Stop incrementing when target is reached
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }
  }

  startCounter() {
    if (this.started) return;
    this.started = true;
    
    setTimeout(() => {
      // Start the interval that increments every X seconds
      this.intervalId = setInterval(() => {
        this.incrementCounter();
      }, this.interval);
    }, this.delay);
  }
}

// Register the custom element
customElements.define('counter-component', CounterComponent);